// Pantalla de consent cuando un jugador acepta una invitación via token.
// Se muestra como overlay sobre la app cuando hay ?teamToken=xxx en la URL.
// Crítico: es la pantalla que determina si el feature es confiable.

import React, { useState, useEffect } from 'react';
import { useT } from '../../lib/i18n';
import { supabase, SUPABASE_ENABLED } from '../../lib/supabase';
import { User } from '@supabase/supabase-js';

interface InviteData {
  teamId:    string;
  teamName:  string;
  coachEmail:string;
  token:     string;
}

interface Props {
  token:   string;
  user:    User | null;
  onDone:  (joined: boolean) => void;
}

type ScreenState = 'loading' | 'valid' | 'expired' | 'invalid' | 'already_member' | 'accepting' | 'seats_full';

export function TeamConsentScreen({ token, user, onDone }: Props) {
  const { t } = useT();
  const [state, setState]       = useState<ScreenState>('loading');
  const [invite, setInvite]     = useState<InviteData | null>(null);
  const [withSync, setWithSync] = useState<boolean | null>(null);
  const [acceptError, setAcceptError] = useState<string | null>(null);

  // ── Validar el token contra Supabase ─────────────────────────────────────────
  useEffect(() => {
    if (!SUPABASE_ENABLED || !token) {
      setState('invalid');
      return;
    }

    let cancelled = false;

    (async () => {
      setState('loading');

      try {
        const apiBase = process.env.REACT_APP_API_URL || '';
        const res = await fetch(`${apiBase}/api/teams/accept`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token }),
        });

        if (cancelled) return;

        const data = await res.json();

        if (res.status === 410) { setState('expired');  return; }
        if (res.status === 409 && data.error === 'invite_already_accepted') {
          // El servidor incluye teamId en la respuesta — verificar membresía activa
          if (user && data.teamId) {
            const { data: membership } = await supabase
              .from('team_memberships')
              .select('status')
              .eq('team_id', data.teamId)
              .eq('user_id', user.id)
              .maybeSingle();
            if (membership?.status === 'active') { setState('already_member'); return; }
          }
          // Token usado por otro usuario o sin sesión — tratar como expirado
          setState('expired');
          return;
        }
        if (!res.ok || !data.valid) { setState('invalid'); return; }

        if (data.error === 'team_seats_exceeded') { setState('seats_full'); return; }

        setInvite({
          teamId:     data.teamId,
          teamName:   data.teamName,
          coachEmail: data.coachEmail,
          token,
        });
        setState('valid');
      } catch {
        if (!cancelled) setState('invalid');
      }
    })();

    return () => { cancelled = true; };
  }, [token, user]);

  // ── Aceptar la invitación ────────────────────────────────────────────────────
  const handleAccept = async (syncEnabled: boolean) => {
    if (!user || !invite) return;

    setState('accepting');
    setWithSync(syncEnabled);

    const doRequest = async () => {
      const { data: sessionData } = await supabase.auth.getSession();
      const jwt = sessionData?.session?.access_token;
      if (!jwt) throw new Error('no_session');
      const apiBase = process.env.REACT_APP_API_URL || '';
      return fetch(`${apiBase}/api/teams/accept`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${jwt}` },
        body: JSON.stringify({ token: invite.token, confirm: true, syncEnabled }),
      });
    };

    try {
      let res = await doRequest();

      // Reintentar una vez en 5xx — Vercel puede tirar FUNCTION_INVOCATION_FAILED
      // en cold start aunque el módulo esté cargado (issue de infraestructura).
      if (res.status >= 500) {
        await new Promise(r => setTimeout(r, 2000));
        res = await doRequest();
      }

      let data: Record<string, any> = {};
      try { data = await res.json(); } catch { /* respuesta no-JSON de Vercel */ }

      if (res.status === 409 && data.error === 'team_seats_exceeded') {
        setState('seats_full');
        return;
      }

      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);

      onDone(true);
    } catch (err) {
      console.error('[TeamConsentScreen] accept error:', err);
      setAcceptError(t('teams.consentAcceptError'));
      setState('valid');
    }
  };

  // ── Render ────────────────────────────────────────────────────────────────────

  const overlayStyle: React.CSSProperties = {
    position: 'fixed',
    inset: 0,
    background: 'rgba(0,0,0,0.75)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    padding: '1rem',
  };

  const cardStyle: React.CSSProperties = {
    background: '#111520',
    border: '1px solid #1e2636',
    borderRadius: '10px',
    padding: '2rem',
    maxWidth: '560px',
    width: '100%',
    maxHeight: '90vh',
    overflowY: 'auto',
    fontFamily: 'var(--mono)',
  };

  if (state === 'loading') {
    return (
      <div style={overlayStyle}>
        <div style={cardStyle}>
          <div style={{ textAlign: 'center', color: 'var(--muted)', fontSize: '0.85rem' }}>
            {t('teams.loading')}
          </div>
        </div>
      </div>
    );
  }

  if (state === 'expired') {
    return (
      <div style={overlayStyle}>
        <div style={cardStyle}>
          <p style={{ color: '#e0a852', marginBottom: '1rem' }}>{t('teams.consentExpired')}</p>
          <button className="btn btn-secondary" onClick={() => onDone(false)}>Cerrar</button>
        </div>
      </div>
    );
  }

  if (state === 'invalid') {
    return (
      <div style={overlayStyle}>
        <div style={cardStyle}>
          <p style={{ color: '#e05252', marginBottom: '1rem' }}>{t('teams.consentInvalid')}</p>
          <button className="btn btn-secondary" onClick={() => onDone(false)}>Cerrar</button>
        </div>
      </div>
    );
  }

  if (state === 'already_member') {
    return (
      <div style={overlayStyle}>
        <div style={cardStyle}>
          <p style={{ color: '#52c2a0', marginBottom: '1rem' }}>{t('teams.consentAlreadyMember')}</p>
          <button className="btn btn-primary" onClick={() => onDone(true)}>Continuar</button>
        </div>
      </div>
    );
  }

  if (state === 'seats_full') {
    return (
      <div style={overlayStyle}>
        <div style={cardStyle}>
          <p style={{ color: '#e0a852', marginBottom: '1rem' }}>{t('teams.consentSeatsFull')}</p>
          <button className="btn btn-secondary" onClick={() => onDone(false)}>Cerrar</button>
        </div>
      </div>
    );
  }

  // Estado 'valid' o 'accepting'
  const isAccepting = state === 'accepting';

  const sectionTitle = (text: string) => (
    <h3 style={{ fontSize: '0.75rem', letterSpacing: '2px', color: 'var(--muted)', margin: '1.25rem 0 0.5rem', textTransform: 'uppercase' }}>
      {text}
    </h3>
  );

  const bullet = (text: string, ok = true) => (
    <div style={{ display: 'flex', gap: '0.6rem', marginBottom: '0.5rem', alignItems: 'flex-start' }}>
      <span style={{ color: ok ? '#52c2a0' : '#e05252', flexShrink: 0, marginTop: '1px' }}>{ok ? '✓' : '✗'}</span>
      <span style={{ fontSize: '0.82rem', color: 'var(--text)', lineHeight: 1.5 }}>{text}</span>
    </div>
  );

  return (
    <div style={overlayStyle}>
      <div style={cardStyle}>
        {/* Badge */}
        <div style={{ marginBottom: '1rem' }}>
          <span style={{ background: '#4a9ae022', border: '1px solid #4a9ae055', color: '#4a9ae0', fontSize: '0.7rem', fontWeight: 700, letterSpacing: '2px', padding: '3px 10px', borderRadius: '3px' }}>
            TEAMS ◈
          </span>
        </div>

        <h2 style={{ margin: '0 0 0.5rem', fontSize: '1.1rem', color: 'var(--text)', letterSpacing: '1px' }}>
          {t('teams.consentTitle', { teamName: invite?.teamName ?? '' })}
        </h2>
        <p style={{ margin: '0 0 1.25rem', fontSize: '0.82rem', color: 'var(--muted)' }}>
          {t('teams.consentSubtitle')}
        </p>

        <div style={{ borderTop: '1px solid #1e2636', paddingTop: '1rem' }}>

          {sectionTitle(t('teams.consentSharedTitle'))}
          {bullet(t('teams.consentShared1'))}
          {bullet(t('teams.consentShared2'))}
          {bullet(t('teams.consentShared3'))}

          {sectionTitle(t('teams.consentNotSharedTitle'))}
          {bullet(t('teams.consentNotShared1'), false)}
          {bullet(t('teams.consentNotShared2'), false)}
          {bullet(t('teams.consentNotShared3'), false)}

          {sectionTitle(t('teams.consentWhen'))}
          <p style={{ fontSize: '0.82rem', color: 'var(--text)', margin: '0 0 0.75rem', lineHeight: 1.5 }}>
            {t('teams.consentWhenBody')}
          </p>

          {sectionTitle(t('teams.consentWho'))}
          <p style={{ fontSize: '0.82rem', color: 'var(--text)', margin: '0 0 0.75rem', lineHeight: 1.5 }}>
            {t('teams.consentWhoBody', { coachEmail: invite?.coachEmail ?? '—' })}
          </p>

          {sectionTitle(t('teams.consentControl'))}
          <p style={{ fontSize: '0.82rem', color: 'var(--text)', margin: '0 0 1.5rem', lineHeight: 1.5 }}>
            {t('teams.consentControlBody')}
          </p>
        </div>

        {acceptError && (
          <div style={{ color: '#e05252', fontSize: '0.8rem', margin: '0 0 0.75rem', lineHeight: 1.5 }}>
            <p style={{ margin: '0 0 0.25rem' }}>{acceptError}</p>
            <p style={{ margin: 0, color: 'var(--muted)', fontSize: '0.75rem' }}>
              {t('teams.consentSupportHint')}{' '}
              <a href="mailto:support@grindlog.app" style={{ color: '#4a9ae0' }}>support@grindlog.app</a>
            </p>
          </div>
        )}

        {/* Botones de acción */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          <button
            className="btn btn-primary"
            disabled={isAccepting}
            onClick={() => handleAccept(true)}
            style={{ width: '100%', justifyContent: 'center' }}
          >
            {isAccepting && withSync ? t('teams.consentAccepting') : t('teams.consentAcceptSync')}
          </button>
          <button
            className="btn btn-secondary"
            disabled={isAccepting}
            onClick={() => handleAccept(false)}
            style={{ width: '100%', justifyContent: 'center', fontSize: '0.8rem' }}
          >
            {isAccepting && !withSync ? t('teams.consentAccepting') : t('teams.consentAcceptNoSync')}
          </button>
        </div>
      </div>
    </div>
  );
}
